import { rosterStandingOf, errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "@/lib/icons";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CommunityChoice } from "@/components/app/fields/community-choice";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/app/patterns/data-table";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { AddToRosterDialog } from "./-components/add-to-roster-dialog";
import { EditRosterDialog, type RosterRow } from "./-components/edit-roster-dialog";
import { RemoveFromRosterDialog } from "./-components/remove-from-roster-dialog";
import { RemovedFromRoster } from "./-components/removed-from-roster";
import { rosterColumns, type RosterEntry } from "./-components/roster-columns";
import { RosterRowActions } from "@/routes/_authenticated/_front-desk/sarah/staff/-components/roster-row-actions";
import { TransferHealthCard } from "./-components/transfer-health-card";

export const Route = createFileRoute("/_authenticated/_front-desk/sarah/staff/")({
  component: Roster,
});

const fail = (error: Error) => toast.error(errorWords(error));

/** Both configuration gaps (nobody holds the fallback, someone on shift has no number) are said
 * above the table, not interrupting it. */
function Roster() {
  const { facilityId } = useFacility();
  const canManage = useCan("frontdesk:manage");
  const canPickNumbers = useCan("settings:manage", facilityId === "all" ? null : facilityId);
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<RosterRow | null>(null);
  const [removing, setRemoving] = useState<RosterEntry | null>(null);

  const roster = useQuery(orpc.frontDesk.staff.list.queryOptions({ input: { facilityId } }));
  const coverage = useQuery(orpc.frontDesk.staff.coverage.queryOptions({ input: { facilityId } }));

  const deactivate = useMutation(
    orpc.frontDesk.staff.deactivate.mutationOptions({
      onSuccess: async () => {
        toast.success("Taken off the front desk");
        setRemoving(null);
        await queryClient.invalidateQueries({ queryKey: orpc.frontDesk.staff.key() });
      },
      onError: fail,
    }),
  );

  const uncovered = coverage.data?.filter((row) => row.uncovered) ?? [];
  const rows = roster.data ?? [];
  const current = rows.filter((row) => row.isActive);
  const removed = rows.filter((row) => !row.isActive);
  const active = current.length;
  const columns = rosterColumns({
    roster: rows,
    allBuildings: facilityId === "all",
    canManage,
    canPickNumbers,
    onEdit: setEditing,
  });
  const hasFallback = rows.some((row) => row.isFallback && row.isActive);
  const unreachable = rows.filter(
    (row) => row.isActive && rosterStandingOf(row) === "unreachable",
  ).length;

  return (
    <>
      <PageHeader
        title="Front desk staff"
        count={roster.data ? active : undefined}
        meta="Who a call is put through to, and when."
        actions={
          canManage && facilityId !== "all" ? (
            <Button onClick={() => setAdding(true)}>Add person</Button>
          ) : null
        }
      />
      {facilityId === "all" ? null : (
        <AddToRosterDialog open={adding} onOpenChange={setAdding} facilityId={facilityId} />
      )}

      {uncovered.length > 0 ? (
        <Alert className="border-warn-border bg-warn-soft">
          <AlertTriangle className="text-warn" aria-hidden />
          <AlertTitle>Sections nobody covers</AlertTitle>
          <AlertDescription>
            {uncovered.map((row) => `${row.facilityName ?? ""} ${row.section}`.trim()).join(", ")}.
            Everyone in these was removed, so Sarah no longer offers them; a caller asking for one
            is routed like any other call.
          </AlertDescription>
        </Alert>
      ) : null}

      {/* Configuration gaps, said once each above the table: a banner with the
          fix, not a line of red text the eye slides past. */}
      {!roster.isPending && current.length > 0 && !hasFallback ? (
        <Alert className="border-crit-border bg-crit-soft">
          <AlertTriangle className="text-crit" aria-hidden />
          <AlertTitle>Nobody holds the fallback</AlertTitle>
          <AlertDescription>
            A call the receptionist cannot place lands nowhere. Open a person and star them as the
            fallback.
          </AlertDescription>
        </Alert>
      ) : null}

      {unreachable > 0 ? (
        <Alert className="border-crit-border bg-crit-soft">
          <AlertTriangle className="text-crit" aria-hidden />
          <AlertTitle>
            {unreachable === 1
              ? "One active person cannot be reached"
              : `${String(unreachable)} active people cannot be reached`}
          </AlertTitle>
          <AlertDescription>
            No phone number and no extension, so callers cannot be put through. Add one to each.
          </AlertDescription>
        </Alert>
      ) : null}

      <EditRosterDialog row={editing} onClose={() => setEditing(null)} />

      {roster.isError ? (
        /* A failed read is not an empty roster, which would read as "nobody to transfer to". */
        <ReadFailed
          message="Couldn't load the front desk staff."
          error={roster.error}
          onRetry={() => void roster.refetch()}
          retrying={roster.isRefetching}
        />
      ) : (
        <DataTable
          data={current}
          columns={columns}
          noun="people"
          rowId={(row) => row.id}
          pending={roster.isPending}
          renderRowActions={
            canManage
              ? (row) => (
                  <RosterRowActions
                    row={row}
                    onEdit={() => setEditing(row as RosterRow)}
                    onRemove={() => setRemoving(row)}
                  />
                )
              : undefined
          }
          empty={
            <EmptyState
              title="Nobody on the front desk yet"
              description="Until somebody is here the receptionist has nobody to transfer a caller to, so every call becomes a message. Add the person who answers the phone first."
              action={
                canManage && facilityId === "all" ? (
                  <CommunityChoice
                    prompt="Choose the building to add people to."
                    className="w-full"
                  />
                ) : null
              }
            />
          }
        />
      )}

      <RemovedFromRoster
        rows={removed}
        showBuilding={facilityId === "all"}
        onEdit={canManage ? (row) => setEditing(row as RosterRow) : undefined}
      />

      {/* Who the roster above actually reached, which the roster itself
          cannot say: a number that has changed still looks healthy. */}
      {facilityId === "all" ? null : <TransferHealthCard facilityId={facilityId} />}

      {/* The fallback catches every call the receptionist cannot place, so
          taking them off with nobody else starred sends those calls nowhere. */}
      <RemoveFromRosterDialog
        removing={removing}
        pending={deactivate.isPending}
        onClose={() => setRemoving(null)}
        onConfirm={(row) => deactivate.mutate({ assignmentId: row.id })}
      />
    </>
  );
}
